import React, { useEffect, useState } from 'react';
import type { ReactionListItem } from '../../types/reactions';
import { formatDate } from '../../utils/dateFormat';

interface ReactionCardProps {
  reaction: ReactionListItem;
  onClick: (id: number) => void;
}

const sourcePillColor: Record<string, string> = {
  MANUAL_UPLOAD: 'bg-gray-100 text-gray-700',
  INDIGO_ELN: 'bg-blue-100 text-blue-700',
  ORD: 'bg-purple-100 text-purple-700',
  SURF: 'bg-amber-100 text-amber-700',
};

const svgCache = new Map<string, string>();

const ReactionThumbnail: React.FC<{ smiles: string }> = ({ smiles }) => {
  const [svg, setSvg] = useState<string | null>(null);
  const cacheKey = `card|${smiles}`;
  const cached = svgCache.get(cacheKey);

  useEffect(() => {
    if (cached || !smiles) return;
    let cancelled = false;
    const fetchSvg = async () => {
      try {
        const { default: apiClient } = await import('../../api/client');
        const res = await apiClient.post('/v2/indigo/render', {
          structure: { value: smiles, type: 'RAW' },
          width: '320px',
          height: '120px',
        }, { responseType: 'text' });
        if (!cancelled) {
          svgCache.set(cacheKey, res.data);
          setSvg(res.data);
        }
      } catch {
        // silent
      }
    };
    fetchSvg();
    return () => { cancelled = true; };
  }, [smiles, cacheKey, cached]);

  const displaySvg = cached || svg;

  if (displaySvg) {
    return (
      <div
        className="reaction-thumbnail w-full h-full flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-full"
        dangerouslySetInnerHTML={{ __html: displaySvg }}
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center">
      <div className="w-5 h-5 border-2 border-gray-200 border-t-emerald-400 rounded-full animate-spin" />
    </div>
  );
};

const ReactionCard: React.FC<ReactionCardProps> = ({ reaction, onClick }) => {
  const { counts, conditions, outcome, enrichment_rollup: er } = reaction;

  const signature = counts.reactants || counts.products
    ? `${counts.reactants} \u2192 ${counts.products}${counts.catalysts ? ' + cat' : ''}`
    : 'No data';

  const matchedRatio = er.total_participants > 0
    ? `${er.matched_participants}/${er.total_participants}`
    : null;

  return (
    <div
      onClick={() => onClick(reaction.id)}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl border border-gray-100 dark:border-gray-700 hover:border-emerald-200 cursor-pointer transition-shadow duration-200 hover:-translate-y-0.5 overflow-hidden flex flex-col"
    >
      {/* Thumbnail */}
      <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center relative">
        {reaction.reaction_smiles ? (
          <ReactionThumbnail smiles={reaction.reaction_smiles} />
        ) : (
          <span className="text-2xl font-bold text-gray-300 tracking-wider">{signature}</span>
        )}
        {outcome.yield_percent != null && (
          <span className="absolute top-2 right-2 px-2 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
            {outcome.yield_percent}%
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col gap-2">
        {/* Reaction type label */}
        {reaction.reaction_type && (
          <span className="self-start px-2.5 py-0.5 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
            {reaction.reaction_type}
          </span>
        )}

        {/* Conditions row */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
          {conditions.solvents?.length ? (
            <span title="Solvent">{conditions.solvents.join(', ')}</span>
          ) : null}
          {conditions.temperature_c != null && <span>{conditions.temperature_c} °C</span>}
          {conditions.time_h != null && <span>{conditions.time_h} h</span>}
        </div>

        {/* Enrichment badges */}
        <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
          {matchedRatio && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              {matchedRatio}
            </span>
          )}
          {er.patent_collisions.total > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {er.patent_collisions.total} collisions
            </span>
          )}
          {er.biology.targets > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
              {er.biology.targets} targets
            </span>
          )}
        </div>

        {/* Source + date */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-50 mt-1">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${sourcePillColor[reaction.source] || 'bg-gray-100 text-gray-600'}`}>
            {reaction.source.replace('_', ' ')}
          </span>
          <span className="text-xs text-gray-400">
            {formatDate(reaction.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReactionCard;
